import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { CollapsibleSection } from "@/components/collapsible-section";
import { trpc } from "@/trpc/trpc";

const alertLabels = {
  no_panel_activity: "Nie loguje się do panelu",
  no_client_activity: "Klienci nie otwierają aplikacji",
  no_orders: "Brak zamówień",
  no_first_order: "Bez pierwszego zamówienia",
} as const;

const DAY_MS = 24 * 60 * 60 * 1000;
const WEEKS = 52;

function ago(iso: string | null) {
  if (!iso) return "—";
  const days = Math.max(
    0,
    Math.floor((Date.now() - new Date(iso).getTime()) / DAY_MS),
  );
  if (days === 0) return "dziś";
  if (days === 1) return "wczoraj";
  return `${days} dni temu`;
}

const shortDate = (day: string) =>
  new Date(`${day}T00:00:00`).toLocaleDateString("pl-PL", {
    day: "numeric",
    month: "short",
  });

const num = (n: number) => n.toLocaleString("pl-PL");

/**
 * Smallest "clean" axis top at or above `max`: 1, 2, 5 × a power of ten —
 * kept even, because these are counts and the middle gridline should not land
 * on half an order.
 */
function niceMax(max: number) {
  if (max <= 4) return 4;
  const pow = 10 ** Math.floor(Math.log10(max));
  const top = [1, 2, 5, 10].find((m) => m * pow >= max)! * pow;
  return top % 2 === 0 ? top : top + 1;
}

type Point = { day: string; value: number };

// One series per chart, so one hue: the mark carries it, text stays in ink.
const SERIES = "text-[#2a78d6] dark:text-[#3987e5]";

const W = 560;
const H = 200;
const PAD = { top: 16, right: 44, bottom: 24, left: 32 };
const PLOT_W = W - PAD.left - PAD.right;
const PLOT_H = H - PAD.top - PAD.bottom;

function Tooltip({ x, point }: { x: number; point: Point }) {
  return (
    <div
      className="pointer-events-none absolute top-0 -translate-x-1/2 rounded-md border bg-popover px-2 py-1 text-xs shadow-sm"
      style={{ left: `${(x / W) * 100}%` }}
    >
      <span className="font-semibold">{num(point.value)}</span>{" "}
      <span className="text-muted-foreground">{shortDate(point.day)}</span>
    </div>
  );
}

function Axes({ top, days }: { top: number; days: string[] }) {
  const ticks = [0, top / 2, top];
  const labelled = [0, Math.floor((days.length - 1) / 2), days.length - 1];
  return (
    <g className="text-muted-foreground" fontSize={11}>
      {ticks.map((t) => {
        const y = PAD.top + PLOT_H - (t / top) * PLOT_H;
        return (
          <g key={t}>
            <line
              x1={PAD.left}
              x2={PAD.left + PLOT_W}
              y1={y}
              y2={y}
              className="stroke-border"
              strokeWidth={1}
            />
            <text
              x={PAD.left - 6}
              y={y + 4}
              textAnchor="end"
              fill="currentColor"
            >
              {num(t)}
            </text>
          </g>
        );
      })}
      {[...new Set(labelled)].map((i) => (
        <text
          key={i}
          x={PAD.left + ((i + 0.5) / days.length) * PLOT_W}
          y={H - 6}
          textAnchor="middle"
          fill="currentColor"
        >
          {shortDate(days[i]!)}
        </text>
      ))}
    </g>
  );
}

/** Weekly totals as columns; the latest week carries its value. */
function WeeklyColumns({ points }: { points: Point[] }) {
  const [hover, setHover] = useState<number | null>(null);
  const top = niceMax(Math.max(...points.map((p) => p.value)));
  const band = PLOT_W / points.length;
  const width = Math.min(24, band - 2);
  const cx = (i: number) => PAD.left + (i + 0.5) * band;
  const y = (v: number) => PAD.top + PLOT_H - (v / top) * PLOT_H;

  return (
    <div className="relative" onPointerLeave={() => setHover(null)}>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img">
        <Axes top={top} days={points.map((p) => p.day)} />
        <g className={SERIES}>
          {points.map((p, i) => {
            const h = PAD.top + PLOT_H - y(p.value);
            const r = Math.min(4, h, width / 2);
            const x = cx(i) - width / 2;
            const base = PAD.top + PLOT_H;
            return (
              <g key={p.day}>
                {h > 0 && (
                  <path
                    d={`M${x},${base} V${y(p.value) + r} q0,${-r} ${r},${-r} H${x + width - r} q${r},0 ${r},${r} V${base} Z`}
                    fill="currentColor"
                    opacity={hover === null || hover === i ? 1 : 0.55}
                  />
                )}
                {/* The whole band is the hit target, not just the bar. */}
                <rect
                  x={PAD.left + i * band}
                  y={PAD.top}
                  width={band}
                  height={PLOT_H}
                  fill="transparent"
                  tabIndex={0}
                  aria-label={`Tydzień do ${shortDate(p.day)}: ${num(p.value)}`}
                  onPointerMove={() => setHover(i)}
                  onFocus={() => setHover(i)}
                  onBlur={() => setHover(null)}
                />
              </g>
            );
          })}
        </g>
        <text
          x={cx(points.length - 1)}
          y={y(points.at(-1)!.value) - 5}
          textAnchor="middle"
          fontSize={11}
          fontWeight={600}
          className="fill-foreground"
        >
          {num(points.at(-1)!.value)}
        </text>
      </svg>
      {hover !== null && <Tooltip x={cx(hover)} point={points[hover]!} />}
    </div>
  );
}

/** A daily level as a line; the crosshair snaps to the nearest day. */
function DailyLine({ points }: { points: Point[] }) {
  const [hover, setHover] = useState<number | null>(null);
  const top = niceMax(Math.max(...points.map((p) => p.value)));
  const band = PLOT_W / points.length;
  const cx = (i: number) => PAD.left + (i + 0.5) * band;
  const y = (v: number) => PAD.top + PLOT_H - (v / top) * PLOT_H;
  const line = points
    .map((p, i) => `${i === 0 ? "M" : "L"}${cx(i)},${y(p.value)}`)
    .join(" ");
  const base = PAD.top + PLOT_H;
  const last = points.length - 1;
  const marked = hover ?? last;

  return (
    <div className="relative" onPointerLeave={() => setHover(null)}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full"
        role="img"
        onPointerMove={(e) => {
          const box = e.currentTarget.getBoundingClientRect();
          const x = ((e.clientX - box.left) / box.width) * W;
          const i = Math.floor((x - PAD.left) / band);
          setHover(Math.min(last, Math.max(0, i)));
        }}
      >
        <Axes top={top} days={points.map((p) => p.day)} />
        <g className={SERIES}>
          <path
            d={`${line} L${cx(last)},${base} L${cx(0)},${base} Z`}
            fill="currentColor"
            opacity={0.1}
          />
          <path
            d={line}
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
          {hover !== null && (
            <line
              x1={cx(hover)}
              x2={cx(hover)}
              y1={PAD.top}
              y2={base}
              className="stroke-muted-foreground"
              strokeWidth={1}
            />
          )}
          <circle
            cx={cx(marked)}
            cy={y(points[marked]!.value)}
            r={5}
            fill="currentColor"
            stroke="var(--card)"
            strokeWidth={2}
          />
        </g>
        <text
          x={cx(last) + 10}
          y={y(points[last]!.value) + 4}
          fontSize={11}
          fontWeight={600}
          className="fill-foreground"
        >
          {num(points[last]!.value)}
        </text>
      </svg>
      {hover !== null && <Tooltip x={cx(hover)} point={points[hover]!} />}
    </div>
  );
}

function Stat({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className="text-xl font-semibold">{value}</p>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

/** How one salon is doing now and over the last year. */
export function SalonHealthSection({ salonId }: { salonId: string }) {
  const { data: history } = trpc.salonHealth.history.useQuery({
    salonId,
    days: WEEKS * 7,
  });
  const { data: overview } = trpc.salonHealth.overview.useQuery();

  if (!history) return null;
  const latest = history.at(-1);
  if (!latest) {
    return (
      <CollapsibleSection id="salon-stats" title="Statystyki salonu">
        <p className="text-sm text-muted-foreground">
          Pierwszy pomiar pojawi się po najbliższym przebiegu.
        </p>
      </CollapsibleSection>
    );
  }

  const alerts = overview?.find((s) => s.salonId === salonId)?.alerts ?? [];

  // `orders7d` is a rolling window, so every seventh snapshot counted back
  // from the newest gives weeks that neither overlap nor leave gaps.
  const weekly: Point[] = [];
  for (let i = history.length - 1; i >= 0 && weekly.length < WEEKS; i -= 7) {
    weekly.unshift({ day: history[i]!.day, value: history[i]!.orders7d });
  }
  const clients: Point[] = history.map((h) => ({
    day: h.day,
    value: h.clientsTotal,
  }));
  const weeklyAverage = latest.ordersPrev28d / 4;

  return (
    <CollapsibleSection
      id="salon-stats"
      title="Statystyki salonu"
      aside={alerts.map((alert) => (
        <Badge key={alert.type} variant="destructive">
          {alertLabels[alert.type]}
        </Badge>
      ))}
    >

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Aktywność w panelu" value={ago(latest.lastPanelActiveAt)} />
        <Stat
          label="Aktywni klienci (30 dni)"
          value={`${latest.clientsActive30d ?? "—"} z ${num(latest.clientsTotal)}`}
          hint={`Nowi w 30 dni: ${num(latest.clientsNew30d)}`}
        />
        <Stat label="Ostatnie zamówienie" value={ago(latest.lastOrderAt)} />
        <Stat
          label="Zamówienia (7 dni)"
          value={num(latest.orders7d)}
          hint={`Średnio tygodniowo wcześniej: ${weeklyAverage.toLocaleString(
            "pl-PL",
            { maximumFractionDigits: 1 },
          )}`}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="gap-2 p-4">
          <div>
            <p className="font-medium">Zamówienia tygodniowo</p>
            <p className="text-xs text-muted-foreground">
Ostatni rok, każdy słupek to 7 dni do podanej daty
            </p>
          </div>
          <WeeklyColumns points={weekly} />
        </Card>
        <Card className="gap-2 p-4">
          <div>
            <p className="font-medium">Klienci w aplikacji</p>
            <p className="text-xs text-muted-foreground">
              Łączna liczba klientów salonu, dzień po dniu, ostatni rok
            </p>
          </div>
          <DailyLine points={clients} />
        </Card>
      </div>

      <details className="text-sm">
        <summary className="cursor-pointer text-muted-foreground">
          Pokaż dane z wykresów
        </summary>
        <table className="mt-2 w-full max-w-md text-sm">
          <thead>
            <tr className="border-b text-left text-muted-foreground">
              <th className="py-1 font-medium">Tydzień do</th>
              <th className="py-1 text-right font-medium">Zamówienia</th>
              <th className="py-1 text-right font-medium">Klienci</th>
            </tr>
          </thead>
          <tbody>
            {weekly.map((w) => (
              <tr key={w.day} className="border-b last:border-0">
                <td className="py-1">{shortDate(w.day)}</td>
                <td className="py-1 text-right tabular-nums">
                  {num(w.value)}
                </td>
                <td className="py-1 text-right tabular-nums">
                  {num(clients.find((c) => c.day === w.day)?.value ?? 0)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </CollapsibleSection>
  );
}
